import { tools as toolEnums } from '../enums';
import subpixel from '../util/canvasSubpixelOffset';
import LineTool from './LineTool';

const { FibToolLabelFormat } = toolEnums.FIBONACCI;

/** Fibonacci Fan tool */
export default class FibonacciFanTool extends LineTool {
	constructor(core, settings) {
		super(core, settings);

		this.slopes = [];
		this.isPointingLeft = false;
		this.isPointingDown = true;
		this.BOUNDING_BOX_HEIGHT = 5;
		this.BIG_VALUE = 100000;
		this.BIG_NEGATIVE_VALUE = -100000;
	}

	_getDefaultParams() {
		return {
			id: 'fibFan',
			name: 'Fibonacci Fan',
			labelFormat: FibToolLabelFormat.DECIMAL,
			style: this.core.getStyle('tool.fibFan')
		};
	}

	/**
	 * @param {array} mouseCoords Coordinates of the active mouse
	 * @param {array} handleCoords Coordinates of the tool's two handles
	 * @returns {boolean} true if the two coordinates intersect
	 */
	isWithinGrid(mouseCoords, handleCoords) {
		const padding = 16;

		const boundingBox = {
			x: Math.min(handleCoords[0][0], handleCoords[1][0]),
			y: Math.min(handleCoords[0][1], handleCoords[1][1]),
			width: Math.abs(handleCoords[0][0] - handleCoords[1][0]),
			height: Math.abs(handleCoords[0][1] - handleCoords[1][1])
		};

		const mouseRect = {
			x: mouseCoords[0] - padding / 2,
			y: mouseCoords[1] - padding / 2,
			width: padding,
			height: padding
		};

		return this.intersect(boundingBox, mouseRect);
	}

	isWithin(mousePanel) {
		const handles = this.handle;
		const coords = [
			this.state.endpointLeft || handles[0].getCoords(),
			this.state.endpointRight || handles[1].getCoords()
		];

		if (!coords[0] || !coords[1]) return false;

		if (this.isWithinGrid(mousePanel, coords)) return true;
	}

	getSpacing(last, curr) {
		const range = Math.abs(last - curr);

		return this.params.style.thresholds.map(t => t * range);
	}

	getFibDistances(fibLevels) {
		const distances = [0];
		let i;

		for (i = 0; i < fibLevels.length - 1; i++) {
			// The +1 is to ensure the color bar covers the white line, tinting it
			distances.push(fibLevels[i + 1] - fibLevels[i] + 1);
		}
		return distances;
	}

	getLineFunction(ctx, x1, x2, y1, y2) {
		const rise = y2 - y1;
		const run = x2 - x1;
		const slope = rise / run;
		const bigConstant = this.isPointingLeft
			? this.BIG_NEGATIVE_VALUE
			: this.BIG_VALUE;

		// add unique slopes to this array so we can color in the slices later
		if (this.slopes.indexOf(slope) < 0) this.slopes.push(slope);

		// could be (x1, y1) OR (x2, y2)
		const yIntercept = y2 - slope * x2;

		return slope * bigConstant + yIntercept;
	}

	drawGridLinePair(ctx, coords, x, y) {
		// Add previous canvas settings to call stack
		ctx.save();

		// Describe grid path
		ctx.beginPath();
		ctx.strokeStyle = '#ccc'; // Allow user to change this style?

		// x grid line (vertical line)
		ctx.moveTo(subpixel.px(x), this.y1);
		ctx.lineTo(subpixel.px(x), subpixel.px(coords[1][1]));

		// y grid line (horizontal line)
		ctx.moveTo(this.x1, subpixel.px(y));
		ctx.lineTo(subpixel.px(coords[1][0]), subpixel.px(y));

		// Draw grid line pair
		ctx.stroke();

		// Restore previous canvas settings from call stack
		ctx.restore();
	}

	drawRay(ctx, x, y) {
		ctx.beginPath();
		ctx.moveTo(this.x1, this.y1);
		const distantY = subpixel.px(
			this.getLineFunction(ctx, this.x1, x, this.y1, y)
		);

		if (this.isPointingLeft) {
			ctx.lineTo(subpixel.px(this.BIG_NEGATIVE_VALUE), distantY);
		} else {
			ctx.lineTo(subpixel.px(this.BIG_VALUE), distantY);
		}
		ctx.stroke();
	}

	_drawControlLine(ctx, coords) {
		if (this.state.hover || this.state.selected) {
			ctx.save();
			ctx.strokeStyle = '#ccc';
			ctx.setLineDash([2, 3]);
			ctx.beginPath();
			ctx.moveTo(this.x1, this.y1);
			ctx.lineTo(subpixel.px(coords[1][0]), subpixel.px(coords[1][1]));
			ctx.stroke();
			ctx.restore();
		}
	}

	drawVerticalAndHorizontalRays(ctx) {
		// Draw vertical ray
		ctx.beginPath();
		if (this.isPointingDown) {
			ctx.moveTo(this.x1, this.y1);
			ctx.lineTo(this.x1, subpixel.px(this.BIG_VALUE));
		} else {
			ctx.moveTo(this.x1, this.y1);
			ctx.lineTo(this.x1, subpixel.px(0));
		}
		ctx.stroke();

		// Draw horizontal ray
		ctx.beginPath();
		if (this.isPointingLeft) {
			ctx.moveTo(this.x1, this.y1);
			ctx.lineTo(subpixel.px(0), this.y1);
		} else {
			ctx.moveTo(this.x1, this.y1);
			ctx.lineTo(subpixel.px(this.BIG_VALUE), this.y1);
		}
		ctx.stroke();
	}

	drawLabels(ctx, i, coords, xVal, yVal) {
		const { yAxis } = this.panel;
		const { thresholds } = this.params.style;
		ctx.font = `${this.params.style.label.font.weight} ${this.params.style.label.font.size}px ${this.params.style.label.font.family}`;
		// Use specific label color if defined, otherwise use the section color
		ctx.fillStyle = this.params.style.label.color || this.params.style.color[i];
		ctx.globalAlpha = 1.0;

		let xTextPadding = 13;
		let yTextPadding = -5;

		// x labels
		ctx.textAlign = 'center';
		if (this.isPointingDown) {
			xTextPadding = -5;
		}

		if (thresholds[i] !== 1) {
			// Don't draw the x-axis' '1' label
			ctx.fillText(
				this._getFibFormatValue(thresholds[i]),
				subpixel.px(coords[1][0] - xVal),
				coords[0][1] + xTextPadding
			);
		}

		// y labels
		ctx.textAlign = 'right';
		if (this.isPointingLeft) {
			ctx.textAlign = 'left';
			yTextPadding = 5;
		}

		// Get yAxis value for label
		const yAxisValue = yAxis.getFormatValue(
			yAxis.primaryScale.invert(subpixel.px(coords[1][1] + yVal)),
			yAxis.getFormatString(yAxis.primaryScale, 5)
		);

		ctx.fillText(
			`${this._getFibFormatValue(thresholds[i])} (${yAxisValue})`,
			coords[0][0] + yTextPadding,
			subpixel.px(coords[1][1] + yVal + 3)
		);
	}

	drawColorSlices(ctx) {
		const sortNumber = (a, b) => a - b;
		this.slopes.sort(sortNumber);

		// Set counter variables
		let countForward = 0;
		let countBackward = this.slopes.length - 1;

		// Draw color slices from outside in
		while (countForward < countBackward) {
			// Slopes and thresholds orders are opposite of eachother, so work backward through thresholds.
			// This is cumbersome and should be made cleaner in the future.
			// Calculate proper element for thresholds:
			// 1. Get last element of thresholds (length - 1)
			// 2. Subtract countForward to work backwards
			// 3. Subtract 1 to skip threshold of 1.0
			const thresholdElement = (this.params.style.thresholds.length - 1) - countForward - 1;

			// Set the section line and fill colors
			const color = this.params.style.background.color[thresholdElement];

			// Define start ray
			const startRayFirst = this.slopes[countForward];
			const startRaySecond = this.slopes[countForward + 1];

			// Define end ray
			const endRayFirst = this.slopes[countBackward - 1];
			const endRaySecond = this.slopes[countBackward];

			// Draw start and end slices
			this.drawColorSlice(ctx, startRayFirst, startRaySecond, color);
			this.drawColorSlice(ctx, endRayFirst, endRaySecond, color);

			countForward++;
			countBackward--;
		}
	}

	/**
	 * Draws filled regions between rays
	 * @param {CanvasRenderingContext2D} ctx The tool's context
	 * @param {number} firstSlope Slope of first ray
	 * @param {number} secondSlope Slope of next ray
	 * @param {string} color Fill color
	 */
	drawColorSlice(ctx, firstSlope, secondSlope, color) {
		let run = this.BIG_VALUE;
		let rise1 = run * firstSlope;
		let rise2 = run * secondSlope;

		if (this.isPointingLeft) {
			rise1 *= -1;
			rise2 *= -1;
			run *= -1;
		}

		ctx.save();
		ctx.beginPath();
		ctx.moveTo(this.x1 + run, this.y1 + rise1);
		ctx.lineTo(this.x1, this.y1);
		ctx.lineTo(this.x1 + run, this.y1 + rise2);
		ctx.closePath();
		ctx.fillStyle = color;
		ctx.fill();
		ctx.restore();
	}

	render() {
		// Get coordinates for drawing elements
		const coords = [this.handle[0].getCoords(), this.handle[1].getCoords()];

		// Only draw if coordinates exist
		if (!coords[0] || !coords[1]) return;

		const { thresholds } = this.params.style;
		this.slopes = [];
		this.x1 = subpixel.px(coords[0][0]);
		this.y1 = subpixel.px(coords[0][1]);
		this.isPointingDown = coords[0][1] < coords[1][1];
		this.isPointingLeft = coords[1][0] - coords[0][0] < 0;

		const ctx = this.panel.rootToolContext;
		let yVal;
		let xVal;
		const fibYSpacing = this.getSpacing(coords[0][1], coords[1][1]);
		const fibXSpacing = this.getSpacing(coords[1][0], coords[0][0]);

		// Get line width for tool
		ctx.lineWidth = this.params.style.width;

		// Set default line color
		ctx.strokeStyle = '#ccc';

		// Draw control line for tool
		this._drawControlLine(ctx, coords);

		// Draw left and bottom bounding rays for tool
		this.drawVerticalAndHorizontalRays(ctx, coords);

		for (let i = 0; i < thresholds.length; i++) {
			yVal = this.isPointingDown ? -fibYSpacing[i] : fibYSpacing[i];
			xVal = this.isPointingLeft ? -fibXSpacing[i] : fibXSpacing[i];

			ctx.strokeStyle = this.params.style.color[i];
			ctx.fillStyle = ctx.strokeStyle;

			// Skip drawing the following on last pass through loop
			if (i !== thresholds.length - 1) {
				// the last one wreaks havoc on my slope/intercept function
				this.drawGridLinePair(
					ctx,
					coords,
					coords[1][0] - xVal,
					coords[1][1] + yVal
				);

				// Draw upper quadrant ray
				this.drawRay(ctx, coords[1][0] - xVal, coords[1][1]);

				// Draw lower quadrant ray
				this.drawRay(ctx, coords[1][0], coords[1][1] + yVal);
			}

			// Draw retracement labels around grid
			this.drawLabels(ctx, i, coords, xVal, yVal);
		}

		// Draw the fills between the lines
		this.drawColorSlices(ctx);

		this.handle[0].render();
		this.handle[1].render();
		this.panel.core.onToolRender(this);
	}
}
